/** @fileoverview Displays connected HR system names, types, and environments. */

import type { ConnectedSystem } from '../services/systemsApi';

const systemLabels: Record<string, string> = {
  SAP_HCM: 'SAP HCM',
  SF_EC: 'SuccessFactors',
  SHAREPOINT: 'SharePoint',
  WORKDAY: 'Workday',
};

export const getConnectedSystemLabel = (code: ConnectedSystem['code']) =>
  systemLabels[code] ?? code.replaceAll('_', ' ');

export const getConnectedSystemLogoUrl = (code: ConnectedSystem['code']) =>
  code === 'SHAREPOINT'
    ? '/images/system-logos/SHAREPOINT.svg'
    : `https://cdn.integrtr.com/logos/${code}.png`;

export default function ConnectedSystemDetails({
  systems,
  systemIds,
  className = '',
  typeOnly = false,
}: {
  systems: ConnectedSystem[];
  systemIds?: string[];
  className?: string;
  typeOnly?: boolean;
}) {
  const referencedSystems = systemIds
    ? systemIds.flatMap((id) => {
        const system = systems.find((item) => item.id === id);
        return system ? [system] : [];
      })
    : systems;
  const displayedSystems = typeOnly
    ? [...new Map(referencedSystems.map((system) => [system.code, system])).values()]
    : referencedSystems;

  if (!displayedSystems.length) return null;

  return (
    <div
      role="group"
      aria-label="Connected systems"
      className={`flex flex-wrap items-center gap-x-2 gap-y-1 ${className}`}
    >
      {!typeOnly && (
        <span className="shrink-0 text-xs font-medium text-app-text-muted">Connected systems</span>
      )}
      {displayedSystems.map((system) => (
        <span
          key={typeOnly ? system.code : system.id}
          title={
            typeOnly
              ? getConnectedSystemLabel(system.code)
              : `${system.name} · ${getConnectedSystemLabel(system.code)}${
                  system.environment ? ` · ${system.environment}` : ''
                }`
          }
          className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-app-border bg-app-surface-card px-2 py-1 text-xs text-app-text"
        >
          <img
            src={getConnectedSystemLogoUrl(system.code)}
            alt={getConnectedSystemLabel(system.code)}
            className="h-4 w-auto max-w-full shrink-0 object-contain"
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
          {!typeOnly && <span className="max-w-56 truncate font-medium">{system.name}</span>}
          {!typeOnly && system.environment && (
            <span className="text-app-text-muted">· {system.environment}</span>
          )}
        </span>
      ))}
    </div>
  );
}
